import type { ConversionJob } from "../types/job";
import { downloadHref } from "../api/jobs";
import { formatBytes, formatDate, STAGE_LABELS, STATUS_TONES } from "../utils/format";
import ProgressBar from "./ProgressBar";
import StatusBadge from "./StatusBadge";

interface JobCardProps {
    job: ConversionJob;
    onDelete: (jobId: string) => void;
    deleting?: boolean;
}

const PROGRESS_TONE: Record<string, "info" | "success" | "danger"> = {
    success: "success",
    danger: "danger",
};

export default function JobCard({ job, onDelete, deleting = false }: JobCardProps) {
    const tone = STATUS_TONES[job.status];
    const progressTone = PROGRESS_TONE[tone] ?? "info";
    const showProgress = !["rejected", "failed", "expired", "deleted"].includes(job.status);

    return (
        <li className="rounded-xl border border-gray-200 p-4 shadow-sm dark:border-gray-800">
            <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-semibold text-gray-900 dark:text-gray-100">
                        {job.original_filename}
                    </p>
                    <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">
                        {formatBytes(job.file_size)} &middot; caricato il {formatDate(job.created_at)}
                    </p>
                </div>
                <StatusBadge status={job.status} />
            </div>

            {showProgress && (
                <div className="mt-3">
                    <ProgressBar progress={job.progress} tone={progressTone} />
                    <p className="mt-1.5 text-xs text-gray-500 dark:text-gray-400">
                        {job.status_message ?? STAGE_LABELS[job.stage]} &middot; {job.progress}%
                    </p>
                </div>
            )}

            {(job.status === "rejected" || job.status === "failed") && job.error_message && (
                <p
                    role="alert"
                    className="mt-3 rounded-lg bg-red-50 px-3 py-2 text-xs text-red-700 dark:bg-red-950/40 dark:text-red-300"
                >
                    {job.error_message}
                </p>
            )}

            {job.status === "expired" && (
                <p className="mt-3 rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-700 dark:bg-amber-950/40 dark:text-amber-300">
                    Questo file è scaduto ed è stato eliminato automaticamente.
                </p>
            )}

            <div className="mt-4 flex flex-wrap items-center gap-2">
                {job.download_available && (
                    <a
                        href={downloadHref(job.job_id)}
                        className="inline-flex min-h-touch items-center justify-center gap-2 rounded-lg bg-green-600 px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2"
                    >
                        Scarica Markdown
                    </a>
                )}
                <button
                    type="button"
                    disabled={deleting}
                    onClick={() => onDelete(job.job_id)}
                    className="inline-flex min-h-touch items-center justify-center rounded-lg px-3 py-2 text-sm font-medium text-gray-500 transition hover:bg-gray-100 hover:text-gray-700 disabled:opacity-50 dark:text-gray-400 dark:hover:bg-gray-800 dark:hover:text-gray-200"
                >
                    {deleting ? "Rimozione..." : "Rimuovi"}
                </button>
            </div>
        </li>
    );
}
